/* eslint-disable react/no-array-index-key, react/jsx-props-no-spreading */
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import colors from 'common/constants/colors';
import OFFAGE_REASON_OPTIONS from 'common/constants/offageReasons';
import { EMPTY_OFFAGE_REASON, isDirtyOffageEntry } from '../utils/stockLineValidation';

/**
 * Why a line received fewer units than were shipped. Shared by New Stock Receipt and Add Items, so
 * the two ways of entering a receipt line cannot drift apart again.
 */
const OffageReasonsSection = ({ offage, offageReasons, showErrors, onChange }) => {
  if (offage <= 0) return null;

  const offageSumQty = offageReasons.reduce((s, r) => s + (Number(r.qty) || 0), 0);
  const offageMatched = offageSumQty === offage;
  const updateReason = (reasonIdx, changes) =>
    onChange(offageReasons.map((r, ri) => (ri === reasonIdx ? { ...r, ...changes } : r)));

  return (
    <Accordion
      defaultExpanded
      disableGutters
      elevation={0}
      data-testid='offage-reasons-section'
      sx={{
        mt: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '4px !important',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { mt: 1.5 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          bgcolor: colors.offageReasonsSurface,
          borderRadius: '3px',
          minHeight: '40px !important',
          '& .MuiAccordionSummary-content': {
            my: '6px',
            alignItems: 'center',
            gap: 1,
          },
        }}
      >
        <WarningAmberIcon fontSize='small' sx={{ color: 'warning.main' }} />
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          Offage Reasons
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          · {offageSumQty} of {offage} units accounted
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {offageMatched && (
          <Chip
            icon={<CheckCircleOutlineIcon />}
            label='Balanced'
            color='success'
            size='small'
            sx={{ mr: 1 }}
          />
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2, pb: 1.5 }}>
        {offageReasons.length === 0 && (
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
            Optionally document reasons for this offage.
          </Typography>
        )}

        {offageReasons.map((entry, rIdx) => {
          const otherQtySum = offageReasons.reduce(
            (s, r, ri) => (ri !== rIdx ? s + (Number(r.qty) || 0) : s),
            0
          );
          const maxQtyForEntry = offage - otherQtySum;
          const qtyExceedsMax =
            String(entry.qty).trim() !== '' && Number(entry.qty) > maxQtyForEntry;
          const entryDirty = isDirtyOffageEntry(entry);
          const qtyIsInvalid = String(entry.qty).trim() !== '' && Number(entry.qty) < 1;
          let qtyHelperText;
          if (showErrors && entryDirty && !String(entry.qty).trim()) {
            qtyHelperText = 'Required';
          } else if (qtyIsInvalid) {
            qtyHelperText = 'Min 1';
          } else if (qtyExceedsMax) {
            qtyHelperText =
              maxQtyForEntry <= 0 ? 'Fully accounted for' : `Exceeds limit (max ${maxQtyForEntry})`;
          }
          return (
            <Box key={rIdx} sx={{ my: 2 }}>
              <Grid container spacing={2} alignItems='flex-start'>
                <Grid item xs={3}>
                  <MuiTextField
                    label='Qty *'
                    size='small'
                    fullWidth
                    type='number'
                    inputProps={{
                      min: 1,
                      style: { textAlign: 'right' },
                    }}
                    value={entry.qty}
                    onChange={(e) => updateReason(rIdx, { qty: e.target.value })}
                    error={
                      (showErrors && entryDirty && !String(entry.qty).trim()) ||
                      qtyIsInvalid ||
                      qtyExceedsMax
                    }
                    helperText={qtyHelperText}
                  />
                </Grid>
                <Grid item xs={8}>
                  <Autocomplete
                    multiple
                    freeSolo
                    disableCloseOnSelect
                    size='small'
                    options={OFFAGE_REASON_OPTIONS}
                    value={entry.reasons}
                    onChange={(_, newReasons) => updateReason(rIdx, { reasons: newReasons })}
                    filterSelectedOptions
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip key={index} label={option} size='small' {...getTagProps({ index })} />
                      ))
                    }
                    renderInput={(params) => (
                      <MuiTextField
                        {...params}
                        label='Reasons *'
                        error={showErrors && entryDirty && entry.reasons.length === 0}
                        helperText={
                          showErrors && entryDirty && entry.reasons.length === 0
                            ? 'Required'
                            : undefined
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid
                  item
                  xs={1}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pt: '6px !important',
                  }}
                >
                  <IconButton
                    size='small'
                    color='error'
                    aria-label='Remove reason'
                    onClick={() => onChange(offageReasons.filter((_, ri) => ri !== rIdx))}
                  >
                    <DeleteOutlineIcon fontSize='small' />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          );
        })}
        <Button
          size='small'
          variant='outlined'
          startIcon={<AddIcon />}
          disabled={offageSumQty >= offage}
          onClick={() => onChange([...offageReasons, { ...EMPTY_OFFAGE_REASON }])}
          sx={{ mt: 0.5 }}
        >
          Add reason
        </Button>
      </AccordionDetails>
    </Accordion>
  );
};

OffageReasonsSection.propTypes = {
  offage: PropTypes.number.isRequired,
  offageReasons: PropTypes.arrayOf(
    PropTypes.shape({
      qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      reasons: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  showErrors: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

OffageReasonsSection.defaultProps = {
  offageReasons: [],
  showErrors: false,
};

export default OffageReasonsSection;
